import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Footer } from '@/components/Footer';
import {
  Wallet,
  Search,
  TrendingUp,
  TrendingDown,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Gift,
  ExternalLink,
  ChevronUp,
  ChevronDown,
  BarChart3,
  Globe,
  Activity,
  RefreshCw,
  CreditCard,
  Smartphone,
  Shield,
  Lock,
  ArrowRight,
  Nfc,
  Apple,
  ChevronRight,
  Zap,
  Menu,
  X,
  Coins,
  ShieldCheck,
} from 'lucide-react';
import { marketAPI } from '@/lib/api';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { LanguageToggle } from '@/components/LanguageToggle';
import { SafeCryptoLogo } from '@/components/SafeCryptoLogo';
import { cn } from '@/lib/utils';
import { Card3D } from '@/components/Card3D';
import Autoplay from 'embla-carousel-autoplay';

function MiniSparkline({ data, positive }) {
  if (!data || data.length < 2) return <div className="w-[120px] h-[40px]" />;

  const sampled = data.length > 40
    ? data.filter((_, i) => i % Math.ceil(data.length / 40) === 0)
    : data;

  const min = Math.min(...sampled);
  const max = Math.max(...sampled);
  const range = max - min || 1;
  const height = 40;
  const width = 120;
  const stepX = width / (sampled.length - 1);

  const points = sampled.map((val, i) => {
    const x = i * stepX;
    const y = height - ((val - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');

  const color = positive ? '#2DD36F' : '#D94A4A';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="flex-shrink-0">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PriceChange({ value }) {
  if (value === null || value === undefined) return <span className="text-[hsl(var(--muted-foreground))]">-</span>;
  const isPositive = value >= 0;
  return (
    <span className={cn(
      "flex items-center gap-0.5 tabular-nums text-sm font-medium",
      isPositive ? "text-[#2DD36F]" : "text-[#D94A4A]"
    )}>
      {isPositive ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      {Math.abs(value).toFixed(2)}%
    </span>
  );
}

function formatNumber(num, options = {}) {
  if (!num && num !== 0) return '-';
  const { style = 'decimal', compact = false, currency = false, decimals } = options;

  if (currency) {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(decimals ?? 2)}`;
  }

  if (compact) {
    if (num >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(decimals ?? 2);
  }

  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 2,
  });
}

function formatPrice(price) {
  if (!price && price !== 0) return '$0.00';
  if (price >= 1000) return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  if (price >= 0.01) return `$${price.toFixed(4)}`;
  return `$${price.toFixed(6)}`;
}

const promoItems = [
  {
    key: 'security',
    icon: ShieldCheck,
    color: '#A78BFA',
    bg: 'from-[rgba(167,139,250,0.1)] to-[rgba(167,139,250,0.03)]',
    border: 'border-[#A78BFA]/20 hover:border-[#A78BFA]/40',
    titleKey: 'promo.securityTitle',
    descKey: 'promo.securityDesc',
    link: '/register',
  },
  {
    key: 'cards',
    icon: CreditCard,
    color: '#28AAFF',
    bg: 'from-[rgba(40,170,255,0.1)] to-[rgba(40,170,255,0.03)]',
    border: 'border-[#28AAFF]/20 hover:border-[#28AAFF]/40',
    titleKey: 'promo.cardsTitle',
    descKey: 'promo.cardsDesc',
    link: '/register',
  },
  {
    key: 'gamefuel',
    icon: Zap,
    color: '#F7931A',
    bg: 'from-[rgba(247,147,26,0.1)] to-[rgba(247,147,26,0.03)]',
    border: 'border-[#F7931A]/20 hover:border-[#F7931A]/40',
    titleKey: 'promo.gamefuelTitle',
    descKey: 'promo.gamefuelDesc',
    link: '/register',
  },
  {
    key: 'safecoin',
    icon: Coins,
    color: '#2DD36F',
    bg: 'from-[rgba(45,211,111,0.1)] to-[rgba(45,211,111,0.03)]',
    border: 'border-[#2DD36F]/20 hover:border-[#2DD36F]/40',
    titleKey: 'promo.safecoinTitle',
    descKey: 'promo.safecoinDesc',
    link: '/register',
  },
  {
    key: 'bonus',
    icon: Gift,
    color: 'hsl(var(--primary))',
    bg: 'from-[hsl(var(--primary))]/10 to-[rgba(247,200,75,0.06)]',
    border: 'border-[hsl(var(--primary))]/20 hover:border-[hsl(var(--primary))]/40',
    titleKey: 'promo.bonusTitle',
    descKey: 'promo.bonusDesc',
    link: '/register',
  },
];

function PromoBanner() {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % promoItems.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const item = promoItems[current];
  const Icon = item.icon;

  return (
    <div className="relative overflow-hidden" data-testid="promo-banner">
      <Link
        to={item.link}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r ${item.bg} border ${item.border} transition-all duration-500 group`}
      >
        <div
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${item.color}18` }}
        >
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: item.color }} />
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <p className="text-sm font-semibold text-[hsl(var(--foreground))] truncate transition-all duration-500">
            {t(item.titleKey)}
          </p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
            {t(item.descKey)}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Dot indicators */}
          <div className="hidden sm:flex items-center gap-1">
            {promoItems.map((_, i) => (
              <div
                key={i}
                className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: i === current ? item.color : 'rgba(255,255,255,0.15)',
                  width: i === current ? 12 : 6,
                }}
              />
            ))}
          </div>
          <ArrowUpRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" style={{ color: item.color }} />
        </div>
      </Link>
    </div>
  );
}

export default function Landing() {
  const { t, lang } = useLanguage();
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('market_cap_rank');
  const [sortDir, setSortDir] = useState('asc');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cv_favorites') || '[]'); } catch { return []; }
  });

  const loadData = useCallback(async () => {
    try {
      const res = await marketAPI.getOverview();
      setMarketData(res.data);
    } catch (err) {
      console.error('Market data error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 120000);
    return () => clearInterval(interval);
  }, [loadData]);

  const toggleFavorite = (id) => {
    setFavorites((prev) => {
      const next = prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id];
      localStorage.setItem('cv_favorites', JSON.stringify(next));
      return next;
    });
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir(key === 'market_cap_rank' ? 'asc' : 'desc');
    }
  };

  const filteredCoins = useMemo(() => {
    if (!marketData?.coins) return [];
    let coins = [...marketData.coins];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      coins = coins.filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.symbol.toLowerCase().includes(q)
      );
    }

    coins.sort((a, b) => {
      const aVal = a[sortKey] ?? 0;
      const bVal = b[sortKey] ?? 0;
      return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

    return coins;
  }, [marketData, searchQuery, sortKey, sortDir]);

  const stats = marketData?.global_stats;

  const SortHeader = ({ label, sortField, className = '' }) => (
    <TableHead
      className={cn("cursor-pointer select-none hover:text-[hsl(var(--foreground))] transition-colors duration-200", className)}
      onClick={() => handleSort(sortField)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortKey === sortField && (
          sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </span>
    </TableHead>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Top Stats Bar */}
      {stats && (
        <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-6 py-2 overflow-x-auto text-xs text-[hsl(var(--muted-foreground))]">
            <span data-testid="market-stat-cryptos">
              {t('market.activeCryptos')}: <strong className="text-[hsl(var(--foreground))]">{formatNumber(stats.active_cryptos, { compact: true })}</strong>
            </span>
            <span data-testid="market-stat-exchanges">
              {t('market.exchanges')}: <strong className="text-[hsl(var(--foreground))]">{stats.exchanges}</strong>
            </span>
            <span data-testid="market-stat-cap">
              {t('market.marketCap')}: <strong className="text-[hsl(var(--foreground))]">{formatNumber(stats.total_market_cap, { currency: true })}</strong>
            </span>
            <span data-testid="market-stat-volume">
              {t('market.volume24h')}: <strong className="text-[hsl(var(--foreground))]">{formatNumber(stats.total_volume_24h, { currency: true })}</strong>
            </span>
            <span>
              {t('market.btcDominance')}: <strong className="text-[hsl(var(--foreground))]">{stats.btc_dominance}%</strong>
            </span>
            <span>
              {t('market.ethDominance')}: <strong className="text-[hsl(var(--foreground))]">{stats.eth_dominance}%</strong>
            </span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/60 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14 sm:h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <SafeCryptoLogo size={28} className="sm:w-9 sm:h-9" />
            <span className="font-bold text-base sm:text-xl font-['Space_Grotesk'] tracking-tight">Safe Crypto</span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            <Link to="/" className="px-3 py-2 rounded-lg text-sm font-medium text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5">
              {t('market.allCoins')}
            </Link>
            <Link to="/promo" className="px-3 py-2 rounded-lg text-sm font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/5 transition-colors duration-200">
              <Gift className="w-4 h-4 inline mr-1.5" />{t('market.promoLink')}
            </Link>
          </div>

          <div className="hidden sm:flex items-center gap-3">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                data-testid="market-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('market.searchPlaceholder')}
                className="pl-9 w-[200px] lg:w-[280px] bg-[hsl(var(--background))] border-[hsl(var(--border))] h-9 rounded-full text-sm"
              />
            </div>
            <LanguageToggle variant="nav" />
            <Button variant="ghost" asChild className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] h-9">
              <Link to="/login" data-testid="nav-login-button">{t('common.login')}</Link>
            </Button>
            <Button asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-9 px-4">
              <Link to="/register" data-testid="home-hero-register-button">{t('common.register')}</Link>
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="sm:hidden p-2 rounded-lg hover:bg-white/5 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-4 space-y-3 animate-in slide-in-from-top-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('market.searchPlaceholder')}
                className="pl-9 bg-[hsl(var(--background))] border-[hsl(var(--border))] h-10 rounded-lg text-sm w-full"
              />
            </div>
            <div className="flex items-center gap-2">
              <LanguageToggle variant="nav" />
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" className="flex-1 h-11 border-[hsl(var(--border))]">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>{t('common.login')}</Link>
              </Button>
              <Button asChild className="flex-1 h-11 bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>{t('common.register')}</Link>
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Branded Hero Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <SafeCryptoLogo size={22} />
                <span className="text-xs font-semibold text-[hsl(var(--primary))] tracking-wider uppercase">Safe Crypto</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] tracking-tight leading-tight" data-testid="market-title">
                {t('market.title')}
              </h1>
              <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] mt-1.5 flex flex-wrap items-center gap-2">
                {t('market.subtitle')}{' '}
                {stats && (
                  <strong className="text-[hsl(var(--foreground))]">
                    {formatNumber(stats.total_market_cap, { currency: true })}
                  </strong>
                )}
                {marketData?.last_updated && (
                  <span className="flex items-center gap-1">
                    <Activity className="w-3 h-3 text-[#2DD36F]" />
                    <span className="text-xs">{t('market.liveData')}</span>
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Rotating Promo Banner */}
          <PromoBanner />
        </div>

        {/* Feature Announcement Slider */}
        <div className="mb-10" data-testid="feature-slider">
          <Carousel
            opts={{ align: 'start', loop: true }}
            plugins={[Autoplay({ delay: 5000, stopOnInteraction: true })]}
            className="w-full"
          >
            <CarouselContent>
              {/* Slide 1 - Crypto Cards */}
              <CarouselItem>
                <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,24%,9%)] p-4 sm:p-8 lg:p-10 min-h-[240px] sm:min-h-[300px] lg:min-h-[320px]">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 left-[15%] w-[500px] h-[500px] rounded-full bg-[rgba(55,240,196,0.07)] blur-[120px]" />
                    <div className="absolute bottom-0 right-[20%] w-[400px] h-[400px] rounded-full bg-[rgba(40,170,255,0.05)] blur-[100px]" />
                  </div>
                  <div className="relative z-10 flex flex-col lg:flex-row items-center gap-4 lg:gap-10 h-full">
                    <div className="flex-1 space-y-2 sm:space-y-3">
                      <Badge className="bg-[rgba(55,240,196,0.1)] text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 gap-1.5 text-xs">
                        <CreditCard className="w-3 h-3" /> {t('features.badgeNew')}
                      </Badge>
                      <h2 className="font-['Space_Grotesk'] text-lg sm:text-2xl lg:text-3xl font-semibold tracking-tight">
                        {t('features.slide1Title')}
                      </h2>
                      <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                        {t('features.slide1Desc')}
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] gap-1 text-[10px]">
                          <Nfc className="w-3 h-3" /> {t('features.contactless')}
                        </Badge>
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] gap-1 text-[10px]">
                          <Apple className="w-3 h-3" /> {t('features.applePay')}
                        </Badge>
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] gap-1 text-[10px]">
                          <Wallet className="w-3 h-3" /> {t('features.googlePay')}
                        </Badge>
                      </div>
                      <div className="pt-2">
                        <Button asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2 h-10">
                          <Link to="/register" data-testid="feature-slide1-cta">
                            {t('features.slide1Cta')} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0 hidden sm:flex gap-3 items-end">
                      <div className="transform -rotate-6 scale-90 opacity-70">
                        <Card3D tier="grey" variant="physical" mini />
                      </div>
                      <div className="transform rotate-3 z-10">
                        <Card3D tier="gold" variant="physical" />
                      </div>
                      <div className="transform rotate-12 scale-85 opacity-60 hidden lg:block">
                        <Card3D tier="platinum" variant="virtual" mini />
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>

              {/* Slide 2 - Wallet Connect */}
              <CarouselItem>
                <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,24%,9%)] p-4 sm:p-8 lg:p-10 min-h-[240px] sm:min-h-[300px] lg:min-h-[320px]">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-[20%] right-[15%] w-[500px] h-[500px] rounded-full bg-[rgba(40,170,255,0.06)] blur-[120px]" />
                  </div>
                  <div className="relative z-10 flex flex-col lg:flex-row items-center gap-4 lg:gap-10 h-full">
                    <div className="flex-1 space-y-2 sm:space-y-3">
                      <Badge className="bg-[rgba(40,170,255,0.1)] text-[#28AAFF] border-[#28AAFF]/20 gap-1.5 text-xs">
                        <Wallet className="w-3 h-3" /> {t('features.badgeWeb3')}
                      </Badge>
                      <h2 className="font-['Space_Grotesk'] text-lg sm:text-2xl lg:text-3xl font-semibold tracking-tight">
                        {t('features.slide2Title')}
                      </h2>
                      <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                        {t('features.slide2Desc')}
                      </p>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] gap-1.5 text-xs px-3 py-1">
                          MetaMask
                        </Badge>
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] gap-1.5 text-xs px-3 py-1">
                          TronLink
                        </Badge>
                        <Badge className="bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] gap-1.5 text-xs px-3 py-1">
                          WalletConnect
                        </Badge>
                      </div>
                      <div className="pt-2">
                        <Button asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2 h-10">
                          <Link to="/register" data-testid="feature-slide2-cta">
                            {t('features.slide2Cta')} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0 hidden sm:block">
                      <div className="w-[280px] h-[180px] rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,20%,12%)] p-6 flex flex-col justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                            <Wallet className="w-5 h-5 text-[hsl(var(--primary))]" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{t('features.web3Login')}</p>
                            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('features.secureInstant')}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <div className="flex-1 h-2 rounded-full bg-[hsl(var(--primary))]/20">
                            <div className="h-full w-3/4 rounded-full bg-[hsl(var(--primary))] transition-all" />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-[hsl(var(--primary))]" />
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">{t('features.e2eEncrypted')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>

              {/* Slide 3 - Welcome Bonus */}
              <CarouselItem>
                <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,24%,9%)] p-4 sm:p-8 lg:p-10 min-h-[240px] sm:min-h-[300px] lg:min-h-[320px]">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-[30%] w-[500px] h-[500px] rounded-full bg-[rgba(247,200,75,0.06)] blur-[120px]" />
                  </div>
                  <div className="relative z-10 flex flex-col lg:flex-row items-center gap-4 lg:gap-10 h-full">
                    <div className="flex-1 space-y-2 sm:space-y-3">
                      <Badge className="bg-[rgba(247,200,75,0.1)] text-[#F7C84B] border-[#F7C84B]/20 gap-1.5 text-xs">
                        <Gift className="w-3 h-3" /> {t('features.badgeBonus')}
                      </Badge>
                      <h2 className="font-['Space_Grotesk'] text-lg sm:text-2xl lg:text-3xl font-semibold tracking-tight">
                        {t('features.slide3Title')}
                      </h2>
                      <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                        {t('features.slide3Desc')}
                      </p>
                      <div className="pt-2">
                        <Button asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2 h-10">
                          <Link to="/register" data-testid="feature-slide3-cta">
                            {t('features.slide3Cta')} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0 hidden sm:block">
                      <div className="w-[280px] h-[180px] rounded-2xl bg-gradient-to-br from-[rgba(247,200,75,0.1)] to-[rgba(55,240,196,0.05)] border border-[#F7C84B]/20 flex items-center justify-center">
                        <div className="text-center">
                          <p className="text-5xl font-bold font-['Space_Grotesk'] text-[#F7C84B]">$200</p>
                          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{t('features.usdtBonus')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>

              {/* Slide 4 - Rate Protection */}
              <CarouselItem>
                <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,24%,9%)] p-4 sm:p-8 lg:p-10 min-h-[240px] sm:min-h-[300px] lg:min-h-[320px]">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute bottom-0 left-[25%] w-[500px] h-[500px] rounded-full bg-[rgba(45,211,111,0.05)] blur-[120px]" />
                  </div>
                  <div className="relative z-10 flex flex-col lg:flex-row items-center gap-4 lg:gap-10 h-full">
                    <div className="flex-1 space-y-2 sm:space-y-3">
                      <Badge className="bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20 gap-1.5 text-xs">
                        <Lock className="w-3 h-3" /> {t('features.badgeSecurity')}
                      </Badge>
                      <h2 className="font-['Space_Grotesk'] text-lg sm:text-2xl lg:text-3xl font-semibold tracking-tight">
                        {t('features.slide4Title')}
                      </h2>
                      <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                        {t('features.slide4Desc')}
                      </p>
                      <div className="pt-2">
                        <Button asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 gap-2 h-10">
                          <Link to="/register" data-testid="feature-slide4-cta">
                            {t('features.slide4Cta')} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0 hidden sm:block">
                      <div className="w-[280px] h-[180px] rounded-2xl bg-[hsl(222,20%,12%)] border border-[hsl(var(--border))]/60 p-5 flex flex-col justify-between">
                        <div className="flex items-center gap-2">
                          <Lock className="w-5 h-5 text-[#2DD36F]" />
                          <span className="text-sm font-semibold">{t('features.rateLock')}</span>
                          <Badge className="text-[9px] h-4 bg-[rgba(45,211,111,0.1)] text-[#2DD36F] border-[#2DD36F]/20 ml-auto">{t('features.rateLockActive')}</Badge>
                        </div>
                        <div>
                          <p className="text-2xl font-bold font-mono text-[hsl(var(--foreground))]">$38.52</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('features.rateLockDesc')}</p>
                        </div>
                        <div className="flex-1 h-1.5 rounded-full bg-[hsl(var(--secondary))] mt-2">
                          <div className="h-full w-2/3 rounded-full bg-[#2DD36F]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>

              {/* Slide 5 - GameFuel Mode */}
              <CarouselItem>
                <div className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))]/60 bg-[hsl(222,24%,9%)] p-4 sm:p-8 lg:p-10 min-h-[240px] sm:min-h-[300px] lg:min-h-[320px]">
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute bottom-0 right-[20%] w-[500px] h-[500px] rounded-full bg-[rgba(247,147,26,0.06)] blur-[120px]" />
                  </div>
                  <div className="relative z-10 flex flex-col lg:flex-row items-center gap-4 lg:gap-10 h-full">
                    <div className="flex-1 space-y-2 sm:space-y-3">
                      <Badge className="bg-[rgba(247,147,26,0.1)] text-[#F7931A] border-[#F7931A]/20 gap-1.5 text-xs">
                        <Zap className="w-3 h-3" /> {t('gamefuel.slideBadge')}
                      </Badge>
                      <h2 className="font-['Space_Grotesk'] text-lg sm:text-2xl lg:text-3xl font-semibold tracking-tight">
                        {t('gamefuel.slideTitle')}
                      </h2>
                      <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
                        {t('gamefuel.slideDesc')}
                      </p>
                      <div className="pt-2">
                        <Button asChild className="bg-[#F7931A] text-white hover:bg-[#F7931A]/90 gap-2 h-10">
                          <Link to="/register" data-testid="feature-gamefuel-cta">
                            {t('gamefuel.slideCta')} <ArrowRight className="w-4 h-4" />
                          </Link>
                        </Button>
                      </div>
                    </div>
                    <div className="flex-shrink-0 hidden sm:block">
                      <div className="w-[280px] h-[180px] rounded-2xl bg-[hsl(222,20%,12%)] border border-[#F7931A]/20 p-5 flex flex-col justify-between">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-[#F7931A]" />
                          <span className="text-sm font-semibold">{t('gamefuel.buyWithCard')}</span>
                        </div>
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-xs text-[hsl(var(--muted-foreground))]">
                            <span>{t('gamefuel.amount')}</span>
                            <span className="font-mono">$100.00</span>
                          </div>
                          <div className="flex justify-between text-xs text-[hsl(var(--muted-foreground))]">
                            <span>{t('gamefuel.processingFee')}</span>
                            <span className="font-mono">-$3.50</span>
                          </div>
                          <div className="flex justify-between text-xs text-[#F7931A] font-semibold pt-1 border-t border-[hsl(var(--border))]">
                            <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> {t('gamefuel.feeCredit')}</span>
                            <span className="font-mono">+$3.50</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselPrevious className="hidden lg:flex -left-4 border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--secondary))]" />
            <CarouselNext className="hidden lg:flex -right-4 border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:bg-[hsl(var(--secondary))]" />
          </Carousel>
        </div>

        {/* Crypto Cards Showcase Section */}
        <div className="mb-8 sm:mb-10" data-testid="cards-showcase-section">
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <div>
              <h2 className="font-['Space_Grotesk'] text-lg sm:text-2xl font-bold tracking-tight flex items-center gap-2">
                <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-[hsl(var(--primary))]" />
                {t('features.cardsSection')}
              </h2>
              <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] mt-1">
                {t('features.cardsSectionDesc')}
              </p>
            </div>
            <Button asChild variant="ghost" className="text-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5 gap-1 hidden sm:flex" data-testid="cards-view-all-link">
              <Link to="/register">
                {t('features.viewAllCards')} <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Physical Cards */}
            {[
              { tier: 'grey', nameKey: 'features.greyCard', commission: 7, limit: '$2K - $5K' },
              { tier: 'gold', nameKey: 'features.goldCard', commission: 5, limit: '$5K - $20K' },
              { tier: 'business', nameKey: 'features.businessCard', commission: 1, limit: '$10K - $100K' },
            ].map((card) => (
              <Link
                key={`p-${card.tier}`}
                to="/register"
                data-testid={`cards-showcase-physical-${card.tier}`}
                className="group rounded-xl border border-[hsl(var(--border))]/60 bg-[hsl(222,20%,12%)] p-4 hover:border-[hsl(var(--primary))]/30 transition-colors duration-200 block"
              >
                <Card3D tier={card.tier} variant="physical" mini />
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">{t(card.nameKey)}</h3>
                    <Badge className="text-[10px] bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]">
                      {t('features.physicalLabel')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
                    <span>{t('features.commission')}: <span className="text-[hsl(var(--foreground))] font-mono">%{card.commission}</span></span>
                    <span>{t('features.limit')}: <span className="text-[hsl(var(--foreground))] font-mono">{card.limit}</span></span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pt-1">
                    {t('features.getCard')} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}

            {/* Virtual Cards */}
            {[
              { tier: 'grey', nameKey: 'features.greyVirtual', commission: 7, limit: '$250 - $500' },
              { tier: 'silver', nameKey: 'features.silverVirtual', commission: 5, limit: '$500 - $2K' },
              { tier: 'platinum', nameKey: 'features.platinumVirtual', commission: 3, limit: '$2K - $10K' },
            ].map((card) => (
              <Link
                key={`v-${card.tier}`}
                to="/register"
                data-testid={`cards-showcase-virtual-${card.tier}`}
                className="group rounded-xl border border-[hsl(var(--border))]/60 bg-[hsl(222,20%,12%)] p-4 hover:border-[hsl(var(--primary))]/30 transition-colors duration-200 block"
              >
                <Card3D tier={card.tier} variant="virtual" mini />
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">{t(card.nameKey)}</h3>
                    <Badge className="text-[10px] bg-[rgba(40,170,255,0.08)] text-[#28AAFF] border-[#28AAFF]/20">
                      {t('features.virtualLabel')}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
                    <span>{t('features.commission')}: <span className="text-[hsl(var(--foreground))] font-mono">%{card.commission}</span></span>
                    <span>{t('features.limit')}: <span className="text-[hsl(var(--foreground))] font-mono">{card.limit}</span></span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pt-1">
                    {t('features.getCard')} <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile View All */}
          <div className="sm:hidden mt-4 text-center">
            <Button asChild variant="outline" className="border-[hsl(var(--border))] gap-1">
              <Link to="/register">
                {t('features.viewAllCards')} <ChevronRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Crypto Table */}
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-6 h-6 rounded" />
                  <Skeleton className="w-8 h-8 rounded-full" />
                  <Skeleton className="h-5 w-28" />
                  <Skeleton className="h-5 w-20 ml-auto" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-24" />
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="market-crypto-table">
                <TableHeader>
                  <TableRow className="border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/30 hover:bg-[hsl(var(--secondary))]/30">
                    <TableHead className="w-[40px] text-center"></TableHead>
                    <SortHeader label={t('market.rank')} sortField="market_cap_rank" className="w-[50px] text-center" />
                    <SortHeader label={t('market.name')} sortField="name" className="min-w-[180px]" />
                    <SortHeader label={t('market.price')} sortField="current_price" className="text-right min-w-[110px]" />
                    <SortHeader label={t('market.change1h')} sortField="price_change_1h" className="text-right min-w-[80px]" />
                    <SortHeader label={t('market.change24h')} sortField="price_change_24h" className="text-right min-w-[80px]" />
                    <SortHeader label={t('market.change7d')} sortField="price_change_7d" className="text-right min-w-[80px]" />
                    <SortHeader label={t('market.colMarketCap')} sortField="market_cap" className="text-right min-w-[120px]" />
                    <SortHeader label={t('market.colVolume')} sortField="total_volume" className="text-right min-w-[120px]" />
                    <SortHeader label={t('market.supply')} sortField="circulating_supply" className="text-right min-w-[130px]" />
                    <TableHead className="text-center min-w-[140px]">{t('market.last7d')}</TableHead>
                    <TableHead className="w-[70px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCoins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-12 text-[hsl(var(--muted-foreground))]">
                        {t('market.noResults')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCoins.map((coin) => {
                      const is7dPositive = (coin.price_change_7d || 0) >= 0;
                      const isFav = favorites.includes(coin.id);
                      return (
                        <TableRow
                          key={coin.id}
                          data-testid={`market-row-${coin.symbol.toLowerCase()}`}
                          className="border-[hsl(var(--border))] hover:bg-white/[0.02] transition-colors duration-150"
                        >
                          {/* Favorite */}
                          <TableCell className="text-center">
                            <button
                              onClick={() => toggleFavorite(coin.id)}
                              className={cn(
                                "p-1 rounded transition-colors duration-200",
                                isFav ? "text-[#F7C84B]" : "text-[hsl(var(--muted-foreground))]/40 hover:text-[#F7C84B]/60"
                              )}
                            >
                              <Star className={cn("w-4 h-4", isFav && "fill-current")} />
                            </button>
                          </TableCell>

                          {/* Rank */}
                          <TableCell className="text-center text-sm text-[hsl(var(--muted-foreground))] tabular-nums">
                            {coin.rank}
                          </TableCell>

                          {/* Name */}
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {coin.image ? (
                                <img src={coin.image} alt={coin.symbol} className="w-7 h-7 rounded-full flex-shrink-0" loading="lazy" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold">{coin.symbol?.charAt(0)}</span>
                                </div>
                              )}
                              <div>
                                <span className="font-medium text-sm">{coin.name}</span>
                                <span className="text-xs text-[hsl(var(--muted-foreground))] ml-2 uppercase">{coin.symbol}</span>
                              </div>
                            </div>
                          </TableCell>

                          {/* Price */}
                          <TableCell className="text-right tabular-nums text-sm font-medium">
                            {formatPrice(coin.current_price)}
                          </TableCell>

                          {/* 1h */}
                          <TableCell className="text-right">
                            <PriceChange value={coin.price_change_1h} />
                          </TableCell>

                          {/* 24h */}
                          <TableCell className="text-right">
                            <PriceChange value={coin.price_change_24h} />
                          </TableCell>

                          {/* 7d */}
                          <TableCell className="text-right">
                            <PriceChange value={coin.price_change_7d} />
                          </TableCell>

                          {/* Market Cap */}
                          <TableCell className="text-right tabular-nums text-sm">
                            {formatNumber(coin.market_cap, { currency: true })}
                          </TableCell>

                          {/* Volume */}
                          <TableCell className="text-right tabular-nums text-sm">
                            {formatNumber(coin.total_volume, { currency: true })}
                          </TableCell>

                          {/* Supply */}
                          <TableCell className="text-right tabular-nums text-sm text-[hsl(var(--muted-foreground))]">
                            {formatNumber(coin.circulating_supply, { compact: true })} {coin.symbol}
                          </TableCell>

                          {/* Sparkline */}
                          <TableCell className="text-center">
                            <MiniSparkline data={coin.sparkline_7d} positive={is7dPositive} />
                          </TableCell>

                          {/* Trade */}
                          <TableCell>
                            <Button
                              asChild
                              size="sm"
                              className="bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 border-0 h-7 text-xs font-medium px-3"
                            >
                              <Link to="/register">{t('market.trade')}</Link>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Last Updated */}
        {marketData?.last_updated && (
          <div className="flex items-center justify-center gap-2 mt-4 text-xs text-[hsl(var(--muted-foreground))]">
            <RefreshCw className="w-3 h-3" />
            <span>{t('market.lastUpdated')}: {new Date(marketData.last_updated).toLocaleTimeString(lang)}</span>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
